import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
type Variant = 'primary' | 'secondary' | 'accent' | 'ghost' | 'dark';
type Size = 'md' | 'lg';
type BaseProps = {
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  withArrow?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
  fullWidth?: boolean;
};
type AsLinkProps = BaseProps & {
  to: string;
  href?: never;
  type?: never;
  onClick?: never;
  disabled?: never;
};
type AsAnchorProps = BaseProps & {
  href: string;
  to?: never;
  type?: never;
  onClick?: never;
  disabled?: never;
};
type AsButtonProps = BaseProps & {
  to?: never;
  href?: never;
  type?: 'button' | 'submit';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};
type Props = AsLinkProps | AsAnchorProps | AsButtonProps;
const BASE = 'group relative inline-flex items-center justify-center gap-2.5 font-medium tracking-[-0.01em] leading-none rounded-full transition-[transform,box-shadow,background-color,border-color,color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap select-none will-change-transform';
const SIZES: Record<Size, string> = {
  md: 'h-[42px] px-5 text-[14px]',
  lg: 'h-[50px] px-7 text-[15px]'
};
const PRIMARY_SHADOW = '[box-shadow:0_1px_0_0_rgba(255,255,255,0.12)_inset,0_1px_2px_rgba(15,20,16,0.12),0_4px_12px_-4px_rgba(15,20,16,0.22)] hover:[box-shadow:0_1px_0_0_rgba(255,255,255,0.14)_inset,0_2px_4px_rgba(15,20,16,0.16),0_10px_24px_-6px_rgba(15,20,16,0.34)] active:[box-shadow:0_1px_0_0_rgba(255,255,255,0.10)_inset,0_1px_1px_rgba(15,20,16,0.10)]';
const ACCENT_SHADOW = '[box-shadow:0_1px_0_0_rgba(255,255,255,0.18)_inset,0_1px_2px_rgba(20,60,30,0.15),0_4px_12px_-4px_rgba(20,60,30,0.32)] hover:[box-shadow:0_1px_0_0_rgba(255,255,255,0.22)_inset,0_2px_4px_rgba(20,60,30,0.18),0_10px_24px_-6px_rgba(20,60,30,0.42)] active:[box-shadow:0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_1px_rgba(20,60,30,0.14)]';
const VARIANTS: Record<Variant, string> = {
  // Green is the new primary — used for the canonical CTA across the site
  primary: `bg-primary text-primary-foreground hover:-translate-y-px active:translate-y-0 ${ACCENT_SHADOW}`,
  // Backward-compat alias — same as primary
  accent: `bg-primary text-primary-foreground hover:-translate-y-px active:translate-y-0 ${ACCENT_SHADOW}`,
  // Old dark primary, kept for moments where a neutral dark CTA is preferred
  dark: `bg-foreground text-background hover:-translate-y-px active:translate-y-0 ${PRIMARY_SHADOW}`,
  secondary: 'bg-card text-foreground border border-foreground/15 hover:bg-foreground hover:text-background hover:border-foreground hover:-translate-y-px active:translate-y-0 [box-shadow:0_1px_0_0_rgba(255,255,255,0.06)_inset,0_1px_2px_rgba(15,20,16,0.06),0_2px_6px_-2px_rgba(15,20,16,0.10)] hover:[box-shadow:0_1px_0_0_rgba(255,255,255,0.10)_inset,0_2px_6px_rgba(15,20,16,0.18),0_8px_20px_-6px_rgba(15,20,16,0.32)]',
  ghost: 'bg-transparent text-foreground hover:bg-secondary'
};
export function CTA(props: Props) {
  const {
    children,
    variant = 'primary',
    size = 'lg',
    withArrow = false,
    icon,
    iconPosition = 'right',
    className = '',
    fullWidth = false
  } = props;
  const classes = `${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${fullWidth ? 'w-full' : ''} ${className}`;
  const renderIcon = withArrow || icon;
  const iconNode = icon ?? <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2} data-id="element-136" />;
  const content = <>
      {renderIcon && iconPosition === 'left' && <span className="inline-flex items-center -ml-0.5" data-id="element-137">{iconNode}</span>}
      <span data-id="element-138">{children}</span>
      {renderIcon && iconPosition === 'right' && <span className="inline-flex items-center -mr-0.5 transition-transform duration-200 ease-out group-hover:translate-x-[2px]" data-id="element-139">
          {iconNode}
        </span>}
    </>;
  if ('to' in props && props.to) {
    return <Link to={props.to} className={classes} data-id="element-140">
        {content}
      </Link>;
  }
  if ('href' in props && props.href) {
    return <a href={props.href} className={classes} data-id="element-141">
        {content}
      </a>;
  }
  const {
    onClick,
    type = 'button',
    disabled
  } = props as AsButtonProps;
  return <button type={type} onClick={onClick} disabled={disabled} className={classes} data-id="element-142">
      {content}
    </button>;
}